import { Link } from 'react-router-dom';
import useReveal from '../hooks/useReveal';
import './MaintenancePackage.css';
import { config } from '../config';

const BENEFITS = [
  { icon:'🛡️', title:'Proactive Security',    desc:'Real-time malware scanning, firewall protection and instant threat removal before they cause damage.' },
  { icon:'🔄', title:'Always Up-to-Date',     desc:'Plugin, theme, and core updates applied and tested regularly so your site never falls behind.' },
  { icon:'💾', title:'Automated Backups',     desc:'Daily or real-time cloud backups mean your data is always safe and recoverable within minutes.' },
  { icon:'⚡', title:'Speed Monitoring',      desc:'Core Web Vitals tracked and optimised monthly so your site stays fast and ranks well on Google.' },
  { icon:'📊', title:'Monthly Reports',       desc:'Clear, jargon-free reports showing uptime, performance scores, updates applied and work done.' },
  { icon:'🎧', title:'Priority Support',      desc:'Dedicated team available via email, WhatsApp and call - response times matched to your plan.' },
];

const PLANS = [
  {
    name:'Basic Care',
    tagline:'Essential site health for simple websites',
    price:'₹3,999',
    period:'/ month',
    featured:false,
    features:[
      'Monthly Plugin & Theme Updates',
      'Security Scanning (Weekly)',
      'Weekly Automated Backups',
      'Uptime Monitoring (99.9%)',
      '2 Hours Support / Month',
      'Monthly Performance Report',
      'Spam Comment Cleanup',
    ],
    cta:'Get Started',
    ctaClass:'btn-ghost',
  },
  {
    name:'Growth',
    tagline:'For businesses that cannot afford downtime',
    price:'₹5,999',
    period:'/ month',
    featured:true,
    features:[
      'Weekly Updates & Patches',
      'Advanced Security Firewall',
      'Daily Cloud Backups',
      'Core Web Vitals Optimisation',
      '5 Hours Support / Month',
      'SEO Health Check (Monthly)',
      'Priority Response - 24hr',
      'Broken Link Monitoring',
      'Uptime Alert (Real-time)',
      'Minor Content Edits Included',
    ],
    cta:'Get Started',
    ctaClass:'btn-gold',
  },
  {
    name:'Enterprise Care',
    tagline:'Fully managed - we handle everything',
    price:'Custom',
    featured:false,
    features:[
      'Real-Time Monitoring & Alerts',
      'Enterprise Security Suite',
      'Real-Time Cloud Backups',
      'Monthly Speed & SEO Audit',
      'Unlimited Support Hours',
      'Content Updates Included',
      'Emergency Response - 2hr',
      'Quarterly Strategy Review',
      'Dedicated Account Manager',
      'Staging Environment',
      'Database Optimisation',
    ],
    cta:'Contact Us',
    ctaClass:'btn-ghost',
  },
];

const PROCESS = [
  { n:'01', title:'Site Audit',       desc:'We start with a comprehensive audit of your current site - security, speed, SEO and code quality.' },
  { n:'02', title:'Onboarding',       desc:'Credentials are securely collected, backups taken, and monitoring tools installed within 24 hours.' },
  { n:'03', title:'Active Care',      desc:'Monthly work begins - updates, scans, optimisations and support all handled on your schedule.' },
  { n:'04', title:'Monthly Report',   desc:'You receive a clear report every month showing exactly what was done and your sites health score.' },
];

export default function MaintenancePackage(){
  useReveal();

  return(
    <div className="maint-page">

      {/* PAGE HERO */}
      <div className="page-hero">
        <span className="ph-eye">Maintenance Plans</span>
        <h1 className="ph-title">Keep Your Website <span className="g">Running Flawlessly</span></h1>
        <p className="ph-sub">Focus on your business while we handle every technical detail. Proactive care that prevents problems before they cost you customers.</p>
      </div>

      {/* BENEFITS */}
      <section className="maint-benefits">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">Why Maintenance Matters</span>
            <h2 className="s-title">Everything We <span className="g">Take Off Your Plate</span></h2>
          </div>
          <div className="benefits-grid">
            {BENEFITS.map((b,i)=>(
              <div key={b.title} className={`benefit-card reveal d${(i%3)+1}`}>
                <div className="benefit-icon">{b.icon}</div>
                <h3>{b.title}</h3>
                <p>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="maint-pricing">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">Choose Your Plan</span>
            <h2 className="s-title">Maintenance <span className="g">Packages</span></h2>
            <p className="s-body">All plans include a free site audit on signup. Cancel anytime - no lock-in contracts.</p>
          </div>

          <div className="maint-grid">
            {PLANS.map((plan,i)=>(
              <div key={plan.name} className={`maint-card reveal d${i+1}${plan.featured?' feat':''}`}>
                {plan.featured && <div className="badge" style={{marginBottom:'1.5rem'}}>✦ Best Value</div>}

                <div className="maint-tier">{plan.name}</div>
                <p className="maint-tagline">{plan.tagline}</p>

                <div className="maint-price">
                  <span className="maint-amount">{plan.price}</span>
                  <span className="maint-period">{plan.period}</span>
                </div>
                {/* <div className="maint-usd">{plan.usd} USD / month</div> */}

                <div className="maint-divider"/>

                <ul className="maint-feats">
                  {plan.features.map(f=>(
                    <li key={f}>
                      <span className="mf-check">✦</span>
                      {f}
                    </li>
                  ))}
                </ul>

                <Link to={`${config.appUrl}contact`} className={plan.ctaClass} style={{marginTop:'auto'}}>
                  {plan.cta} →
                </Link>
              </div>
            ))}
          </div>

          <p className="price-note reveal">
            Need a custom maintenance plan for a large platform or multiple sites?{' '}
            <Link to={`${config.appUrl}contact`} style={{color:'var(--gold)'}}>Let's talk →</Link>
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="maint-process">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">Getting Started</span>
            <h2 className="s-title">How Onboarding <span className="g">Works</span></h2>
          </div>
          <div className="maint-process-grid">
            {PROCESS.map((p,i)=>(
              <div key={p.n} className={`proc-card reveal d${i+1}`} data-n={p.n}>
                <div className="proc-num">{p.n}</div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <div className="trust-strip">
        <div className="container">
          <div className="trust-inner reveal">
            {['99.9% Uptime Guaranteed','No Lock-In Contracts','Free Site Audit on Signup','Cancel Anytime'].map(t=>(
              <div key={t} className="trust-item">
                <span className="trust-check">✦</span>
                {t}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="cta-sect">
        <div className="cta-glow"/>
        <div className="container" style={{position:'relative',textAlign:'center'}}>
          <div className="reveal">
            <h2 className="cta-title">Start Protecting Your <span className="g">Website Today</span></h2>
            <p className="cta-body">Get a free site audit and let us show you exactly what we would fix and improve.</p>
            <div style={{display:'flex',gap:'1rem',justifyContent:'center',flexWrap:'wrap'}}>
              <Link to={`${config.appUrl}contact`} className="btn-primary">Get Free Site Audit →</Link>
              <Link to={`${config.appUrl}web-development-package`} className="btn-outline">View Web Dev Plans</Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}